/**
 * Sticks the car to the road surface.
 *
 * Every frame: sample the surface under each of the four wheels, set the body
 * height from their average, and derive pitch and roll from their differences.
 * That single step produces any measured longitudinal gradient, the slight
 * lean from the 2.5% crossfall, and the settle over a vertical curve - none
 * of which are animated or faked.
 *
 * If no wheel finds a surface, the car is off the road. There is no kerb
 * collision body to hit; the car simply slows and is nudged back toward the
 * last place it was on the road. That is a deliberate substitute for physics:
 * a kerb you cannot mount is also a kerb you cannot get stuck on.
 */
import type { GroundHit, GroundSampler } from '../road/collider';
import { wheelPositions, type CarState } from './arcadeCar';
import { CAR_GEOMETRY, ROAD, VEHICLE } from '../core/spec';

export interface FollowResult {
  /**
   * Longitudinal slope under the car, to feed back into `stepCar`.
   * Clamped to the TPDM absolute maximum gradient - see the note at the return.
   */
  grade: number;
  /** The unclamped measurement, for diagnostics. */
  rawGrade: number;
  /** How many of the four wheels are on the carriageway (not the footway). */
  wheelsOnRoad: number;
}

const TRACK_M = CAR_GEOMETRY.TRACK_M;

/** How quickly the body settles onto a new height, per second. Damps mesh noise. */
const HEIGHT_SMOOTH = 12;
const ATTITUDE_SMOOTH = 8;

/** Recovery when off the road, m/s toward the last on-road position. */
const RECOVER_SPEED = 1.5;

interface Memory {
  lastGoodX: number;
  lastGoodZ: number;
  lastGoodY: number;
  initialised: boolean;
}

export function createFollowMemory(): Memory {
  return { lastGoodX: 0, lastGoodZ: 0, lastGoodY: 0, initialised: false };
}

function damp(current: number, target: number, rate: number, dt: number): number {
  // Frame-rate independent exponential smoothing.
  return target + (current - target) * Math.exp(-rate * dt);
}

export function groundFollow(
  car: CarState,
  sampler: GroundSampler,
  memory: Memory,
  dt: number,
): FollowResult {
  const wheels = wheelPositions(car);
  let hits: (GroundHit | null)[] = wheels.map(([x, z]) => sampler.sample(x, z));
  let found = hits.filter((h): h is GroundHit => h !== null);
  let terrainOnly = false;

  // Beyond the complete road profile, sampled bare ground is still a real
  // surface. It keeps the car planted while the existing off-road resistance
  // model makes clear that this is not a shortcut.
  if (found.length === 0 && sampler.sampleTerrain) {
    hits = wheels.map(([x, z]) => sampler.sampleTerrain!(x, z));
    found = hits.filter((h): h is GroundHit => h !== null);
    terrainOnly = found.length > 0;
  }

  if (found.length === 0) {
    car.offRoad = true;
    if (memory.initialised) {
      // Nudge back toward the road rather than blocking or teleporting. The
      // player keeps control; they just cannot make progress out here.
      const dx = memory.lastGoodX - car.x;
      const dz = memory.lastGoodZ - car.z;
      const d = Math.hypot(dx, dz);
      if (d > 0.01) {
        const step = Math.min(d, RECOVER_SPEED * dt);
        car.x += (dx / d) * step;
        car.z += (dz / d) * step;
      }
      car.y = damp(car.y, memory.lastGoodY, HEIGHT_SMOOTH, dt);
    }
    car.pitch = damp(car.pitch, 0, ATTITUDE_SMOOTH, dt);
    car.roll = damp(car.roll, 0, ATTITUDE_SMOOTH, dt);
    return { grade: 0, rawGrade: 0, wheelsOnRoad: 0 };
  }

  const carriagewayHits = found.filter((hit) => hit.surface === 'carriageway');
  // A wheel brushing a kerb must not turn a kinematic car into a recovery
  // problem. A vehicle wholly on the footway is still grounded, but receives
  // the existing off-road resistance and nudges back toward the carriageway.
  car.offRoad = terrainOnly || carriagewayHits.length === 0;

  // Height: mean of the wheels that found road. Wheels that missed (a wheel
  // over the kerb line) are ignored rather than treated as a hole - which is
  // exactly the case that drops a rigid-body car underground.
  const meanY = found.reduce((a, h) => a + h.y, 0) / found.length;

  // On the very first frame, SNAP. Damping from the initial y = 0 toward a
  // road ~34 m above the datum spends the first half second with the camera
  // below the road surface, looking up at its underside - which is precisely
  // the "car dropped underground" symptom this design exists to avoid.
  car.y = memory.initialised ? damp(car.y, meanY, HEIGHT_SMOOTH, dt) : meanY;

  if (!car.offRoad) {
    memory.lastGoodX = car.x;
    memory.lastGoodZ = car.z;
    memory.lastGoodY = meanY;
    memory.initialised = true;
  }

  // Attitude from wheel height differences. Falls back to the surface normal
  // when some wheels are off, which keeps the body level instead of lurching.
  const [fl, fr, rl, rr] = hits;
  let targetPitch: number;
  let targetRoll: number;

  if (fl && fr && rl && rr) {
    const front = (fl.y + fr.y) / 2;
    const rear = (rl.y + rr.y) / 2;
    const left = (fl.y + rl.y) / 2;
    const right = (fr.y + rr.y) / 2;
    // Nose up on a climb -> negative pitch about the +X axis in three's frame.
    targetPitch = -Math.atan2(front - rear, VEHICLE.WHEELBASE_M);
    targetRoll = Math.atan2(right - left, TRACK_M);
  } else {
    const n = found[0]!;
    const fwdX = Math.sin(car.heading);
    const fwdZ = -Math.cos(car.heading);
    const rightX = Math.cos(car.heading);
    const rightZ = Math.sin(car.heading);
    targetPitch = Math.asin(Math.max(-1, Math.min(1, n.nx * fwdX + n.nz * fwdZ)));
    targetRoll = -Math.asin(Math.max(-1, Math.min(1, n.nx * rightX + n.nz * rightZ)));
  }

  car.pitch = damp(car.pitch, targetPitch, ATTITUDE_SMOOTH, dt);
  car.roll = damp(car.roll, targetRoll, ATTITUDE_SMOOTH, dt);

  // Longitudinal grade in the direction the car is facing, for the gravity term.
  let grade = 0;
  if (fl && fr && rl && rr) {
    const front = (fl.y + fr.y) / 2;
    const rear = (rl.y + rr.y) / 2;
    grade = (front - rear) / VEHICLE.WHEELBASE_M;
  } else {
    const n = found[0]!;
    const fwdX = Math.sin(car.heading);
    const fwdZ = -Math.cos(car.heading);
    // Slope of the plane along the forward direction.
    grade = n.ny > 1e-6 ? -(n.nx * fwdX + n.nz * fwdZ) / n.ny : 0;
  }

  // A general road cannot exceed 10% (TPDM V2 Table 3.3.6.1). A reading above
  // that is never a real hill - it is the wheels straddling two surfaces where
  // the route crosses itself, or bad elevation data in a baked collider. Feed
  // the clamped value to the car so gravity stays sane, and leave the raw
  // number visible to whoever is debugging the terrain.
  const clamped = Math.max(-ROAD.MAX_GRADIENT, Math.min(ROAD.MAX_GRADIENT, grade));

  return { grade: clamped, rawGrade: grade, wheelsOnRoad: carriagewayHits.length };
}
